/**
 * Product Video Component
 *
 * A single reel-style video card shown on the product page with:
 * - Lazy loading via IntersectionObserver
 * - Autoplay (muted) when scrolled into view, pause when out of view
 * - Tap/click to play/pause
 * - Mute/unmute toggle
 */
import { Component } from '@theme/component';

class ProductVideoComponent extends Component {
  /** @type {IntersectionObserver | undefined} */
  #visibilityObserver;

  /** @type {IntersectionObserver | undefined} */
  #lazyObserver;

  /** @type {boolean} */
  #isMuted = true;

  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;

    this._card = this.querySelector('[data-video-card]');
    if (!this._card) return;

    this.#visibilityObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            this.#playVideo();
          } else {
            this.#pauseVideo();
          }
        }
      },
      { threshold: [0, 0.6] }
    );

    this.#lazyObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#loadVideo();
            this.#lazyObserver?.unobserve(entry.target);
          }
        }
      },
      { rootMargin: '200px' }
    );

    this.#visibilityObserver.observe(this._card);
    this.#lazyObserver.observe(this._card);

    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.hidden) {
          this.#pauseVideo();
        }
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#visibilityObserver?.disconnect();
    this.#lazyObserver?.disconnect();
  }

  // =============== NATIVE DELEGATED EVENT HANDLERS ===============

  toggleMute(event) {
    event.stopPropagation();
    this.#isMuted = !this.#isMuted;
    this.#applyMuteState();
  }

  togglePlayPause(event) {
    if (event.target.closest('[data-mute-toggle]')) return;
    event.stopPropagation();
    if (this._card?.dataset.playing === 'true') {
      this.#pauseVideo();
    } else {
      this.#playVideo();
    }
  }

  // =============== VIDEO CONTROL ===============

  #loadVideo() {
    const card = this._card;
    if (!card || card.dataset.videoLoaded === 'true') return;

    const template = card.querySelector('template[data-video-template]');
    if (!template) return;

    const content = /** @type {HTMLTemplateElement} */ (template).content.cloneNode(true);
    card.querySelector('[data-video-container]')?.appendChild(content);
    card.dataset.videoLoaded = 'true';
  }

  #playVideo() {
    const card = this._card;
    if (!card) return;

    if (card.dataset.videoLoaded !== 'true') {
      this.#loadVideo();
    }

    const video = card.querySelector('video');
    if (!video) return;

    video.muted = this.#isMuted;

    const playPromise = video.play();
    if (playPromise) {
      playPromise.catch(() => {
        video.muted = true;
        this.#isMuted = true;
        this.#applyMuteState();
        video.play().catch(() => {});
      });
    }

    card.dataset.playing = 'true';
    this.#updatePlayPauseIcon(true);
  }

  #pauseVideo() {
    const card = this._card;
    if (!card) return;

    const video = card.querySelector('video');
    if (video) video.pause();

    card.dataset.playing = 'false';
    this.#updatePlayPauseIcon(false);
  }

  #applyMuteState() {
    const card = this._card;
    if (!card) return;

    const video = card.querySelector('video');
    if (video) video.muted = this.#isMuted;

    const muteBtn = card.querySelector('[data-mute-toggle]');
    if (muteBtn) {
      const volIcon = muteBtn.querySelector('[data-icon-volume]');
      const muteIcon = muteBtn.querySelector('[data-icon-muted]');
      if (volIcon) volIcon.classList.toggle('hidden', this.#isMuted);
      if (muteIcon) muteIcon.classList.toggle('hidden', !this.#isMuted);
    }
  }

  /** @param {boolean} isPlaying */
  #updatePlayPauseIcon(isPlaying) {
    const btn = this._card?.querySelector('[data-play-toggle]');
    if (!btn) return;
    const playIcon = btn.querySelector('[data-icon-play]');
    const pauseIcon = btn.querySelector('[data-icon-pause]');
    if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
    if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
  }
}

if (!customElements.get('product-video-component')) {
  customElements.define('product-video-component', ProductVideoComponent);
}
